import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  useTheme,
  alpha,
  CircularProgress,
  Alert,
  Skeleton as MuiSkeleton,
  Pagination,
  PaginationItem,
  Grid,
  TextField,
  MenuItem,
  Button,
  Divider,
} from '@mui/material';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import HistoryIcon from '@mui/icons-material/History';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { toast } from 'react-toastify';
import {
  get_profile,
  get_wallet_transactions,
  submit_withdrawal_request,
  get_withdrawal_history,
} from '../../../api/ControllApi';


const Wallet = () => {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [userProfile, setUserProfile] = useState(null);
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 10,
    total: 0,
    last_page: 1,
  });

  // Withdrawal requests states
  const [withdrawalRequests, setWithdrawalRequests] = useState([]);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [submittingWithdrawal, setSubmittingWithdrawal] = useState(false);
  const [paymentDetails, setPaymentDetails] = useState({
    holder_name: '',
    upi_id: '',
    bank_name: '',
    account_number: '',
    ifsc_code: '',
    branch_name: '',
    mobile_number: '',
  });

  const fetchProfile = async () => {
    try {
      const res = await get_profile();
      if (res && res.status) {
        setUserProfile(res.data);
      }
    } catch (err) {}
  };

  const fetchProfileSilent = async () => {
    try {
      const res = await get_profile();
      if (res && res.status) {
        setUserProfile(res.data);
      }
    } catch (err) {}
  };

  const fetchTransactions = async (page = 1) => {
    try {
      setLoading(true);
      const res = await get_wallet_transactions(page);
      if (res && res.status) {
        setTransactions(res.data || []);
        if (res.pagination) {
          setPagination(res.pagination);
        }
      } else {
        setError(res?.message || 'Failed to load transactions');
      }
    } catch (err) {
      setError('An error occurred while fetching your transactions.');
    } finally {
      setLoading(false);
    }
  };

  const fetchTransactionsSilent = async (page = 1) => {
    try {
      const res = await get_wallet_transactions(page);
      if (res && res.status) {
        setTransactions(res.data || []);
        if (res.pagination) {
          setPagination(res.pagination);
        }
      }
    } catch (err) {}
  };

  const fetchWithdrawalHistory = async () => {
    try {
      const res = await get_withdrawal_history();
      if (res && res.status) {
        setWithdrawalRequests(res.data || []);
      }
    } catch (err) {}
  };

  const fetchWithdrawalHistorySilent = async () => {
    try {
      const res = await get_withdrawal_history();
      if (res && res.status) {
        setWithdrawalRequests(res.data || []);
      }
    } catch (err) {}
  };

  useEffect(() => {
    fetchProfile();
    fetchTransactions(1);
    fetchWithdrawalHistory();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      fetchProfileSilent();
      fetchTransactionsSilent(pagination.current_page);
      fetchWithdrawalHistorySilent();
    }, 8000);

    return () => clearInterval(interval);
  }, [pagination.current_page]);

  const handlePaymentDetailsChange = (field) => (e) => {
    setPaymentDetails((prev) => ({
      ...prev,
      [field]: e.target.value,
    }));
  };

  const handleWithdrawSubmit = async (e) => {
    e.preventDefault();
    if (!withdrawAmount) {
      toast.error('Amount is required');
      return;
    }
    const amt = parseFloat(withdrawAmount);
    if (isNaN(amt) || amt <= 0) {
      toast.error('Amount must be greater than 0');
      return;
    }
    const balance = Number(userProfile?.wallet_balance || 0);
    if (amt > balance) {
      toast.error('Insufficient Wallet Balance');
      return;
    }
    if (!paymentMethod) {
      toast.error('Payment method is required');
      return;
    }

    // Dynamic field validation
    if (!paymentDetails.holder_name || paymentDetails.holder_name.trim() === '') {
      toast.error('Account Holder Name is required');
      return;
    }

    if (paymentMethod === 'UPI') {
      if (!paymentDetails.upi_id || paymentDetails.upi_id.trim() === '') {
        toast.error('UPI ID is required');
        return;
      }
    } else if (paymentMethod === 'Bank Transfer') {
      if (!paymentDetails.bank_name || paymentDetails.bank_name.trim() === '') {
        toast.error('Bank Name is required');
        return;
      }
      if (!paymentDetails.account_number || paymentDetails.account_number.trim() === '') {
        toast.error('Account Number is required');
        return;
      }
      if (!paymentDetails.ifsc_code || paymentDetails.ifsc_code.trim() === '') {
        toast.error('IFSC Code is required');
        return;
      }
    } else if (['Google Pay', 'PhonePe', 'Paytm'].includes(paymentMethod)) {
      if (!paymentDetails.mobile_number || paymentDetails.mobile_number.trim() === '') {
        toast.error('Registered Mobile Number is required');
        return;
      }
    }

    // Format payload details
    const formattedDetails = {
      holder_name: paymentDetails.holder_name,
    };
    if (paymentMethod === 'UPI') {
      formattedDetails.upi_id = paymentDetails.upi_id;
    } else if (paymentMethod === 'Bank Transfer') {
      formattedDetails.bank_name = paymentDetails.bank_name;
      formattedDetails.account_number = paymentDetails.account_number;
      formattedDetails.ifsc_code = paymentDetails.ifsc_code;
      formattedDetails.branch_name = paymentDetails.branch_name;
    } else {
      formattedDetails.mobile_number = paymentDetails.mobile_number;
    }

    setSubmittingWithdrawal(true);
    try {
      const res = await submit_withdrawal_request({
        amount: amt,
        payment_method: paymentMethod,
        payment_details: formattedDetails,
      });

      if (res && res.status) {
        toast.success(res.message || 'Your withdrawal request has been submitted successfully.');
        setWithdrawAmount('');
        setPaymentMethod('');
        setPaymentDetails({
          holder_name: '',
          upi_id: '',
          bank_name: '',
          account_number: '',
          ifsc_code: '',
          branch_name: '',
          mobile_number: '',
        });
        
        // Instant reload
        fetchProfile();
        fetchTransactions(1);
        fetchWithdrawalHistory();
      } else {
        toast.error(res?.message || 'Something went wrong');
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to submit withdrawal request.');
    } finally {
      setSubmittingWithdrawal(false);
    }
  };

  const handlePageChange = (event, newPage) => {
    fetchTransactions(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Calculate total credits and debits from visible data
  const totalCredits = transactions.reduce((sum, tx) => sum + (tx.credit || 0), 0);
  const totalDebits = transactions.reduce((sum, tx) => sum + (tx.debit || 0), 0);

  const handleCopyId = (id) => {
    navigator.clipboard.writeText(id);
  };

  if (loading && transactions.length === 0) {
    return (
      <Box sx={{ maxWidth: 1000, mx: 'auto', p: { xs: 2, md: 4 } }}>
        <MuiSkeleton variant="rectangular" height={250} sx={{ borderRadius: 6, mb: 5 }} />
        <MuiSkeleton variant="text" width={300} height={40} sx={{ mb: 3 }} />
        <MuiSkeleton variant="rectangular" height={400} sx={{ borderRadius: 4 }} />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto', p: { xs: 2, md: 4 } }}>
      {error && (
        <Alert severity="error" sx={{ mb: 4, borderRadius: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Premium Header Section */}
      <Card
        sx={{
          mb: 5,
          borderRadius: 6,
          overflow: 'hidden',
          position: 'relative',
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
          color: 'white',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: -50,
            right: -50,
            width: 250,
            height: 250,
            background: 'rgba(255,255,255,0.1)',
            borderRadius: '50%',
            zIndex: 0,
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: -30,
            left: -30,
            width: 150,
            height: 150,
            background: 'rgba(255,255,255,0.05)',
            borderRadius: '50%',
            zIndex: 0,
          }}
        />

        <CardContent sx={{ p: { xs: 4, md: 6 }, position: 'relative', zIndex: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box>
              <Typography variant="overline" sx={{ letterSpacing: 3, opacity: 0.8, fontWeight: 700 }}>
                Available Balance
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'baseline', mt: 1 }}>
                <Typography variant="h2" sx={{ fontWeight: 800 }}>
                  ₹{Number(userProfile?.wallet_balance)?.toFixed(2) || '0.00'}
                </Typography>
                <Typography variant="h6" sx={{ ml: 2, opacity: 0.8, fontWeight: 500 }}>
                  INR
                </Typography>
              </Box>
            </Box>
            <AccountBalanceWalletIcon sx={{ fontSize: 80, opacity: 0.2 }} />
          </Box>

          <Box sx={{ display: 'flex', gap: 3, mt: 4 }}>
            <Paper
              sx={{
                px: 2,
                py: 1,
                borderRadius: 4,
                bgcolor: 'rgba(255,255,255,0.15)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255,255,255,0.3)',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                color: 'white',
              }}
            >
              <TrendingUpIcon sx={{ color: '#4caf50' }} />
              <Typography variant="body2" fontWeight={600}>
                + ₹{totalCredits.toLocaleString()} Credits
              </Typography>
            </Paper>
            <Paper
              sx={{
                px: 2,
                py: 1,
                borderRadius: 4,
                bgcolor: 'rgba(255,255,255,0.15)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255,255,255,0.3)',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                color: 'white',
              }}
            >
              <TrendingDownIcon sx={{ color: '#ff5252' }} />
              <Typography variant="body2" fontWeight={600}>
                - ₹{totalDebits.toLocaleString()} Debits
              </Typography>
            </Paper>
          </Box>
        </CardContent>
      </Card>

      {/* Withdraw Request Section */}
      <Card sx={{ mb: 5, borderRadius: 6, border: '1px solid #eee', boxShadow: '0 8px 24px rgba(0,0,0,0.03)' }}>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h6" fontWeight={700} gutterBottom sx={{ mb: 3 }}>
            Withdraw Request
          </Typography>
          
          <form onSubmit={handleWithdrawSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Withdraw Amount"
                  type="number"
                  fullWidth
                  value={withdrawAmount}
                  onChange={(e) => setWithdrawAmount(e.target.value)}
                  placeholder="Enter amount to withdraw"
                  required
                  inputProps={{ min: "1", step: "any" }}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  label="Payment Method"
                  fullWidth
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  required
                  variant="outlined"
                >
                  <MenuItem value="UPI">UPI</MenuItem>
                  <MenuItem value="Bank Transfer">Bank Transfer</MenuItem>
                  <MenuItem value="Google Pay">Google Pay</MenuItem>
                  <MenuItem value="PhonePe">PhonePe</MenuItem>
                  <MenuItem value="Paytm">Paytm</MenuItem>
                </TextField>
              </Grid>

              {paymentMethod && (
                <>
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }}>
                      <Chip label={`${paymentMethod} Details`} size="small" />
                    </Divider>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Account Holder Name"
                      fullWidth
                      value={paymentDetails.holder_name}
                      onChange={handlePaymentDetailsChange('holder_name')}
                      required
                      variant="outlined"
                    />
                  </Grid>

                  {paymentMethod === 'UPI' && (
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="UPI ID"
                        fullWidth
                        value={paymentDetails.upi_id}
                        onChange={handlePaymentDetailsChange('upi_id')}
                        placeholder="username@upi"
                        required
                        variant="outlined"
                      />
                    </Grid>
                  )}

                  {paymentMethod === 'Bank Transfer' && (
                    <>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Bank Name"
                          fullWidth
                          value={paymentDetails.bank_name}
                          onChange={handlePaymentDetailsChange('bank_name')}
                          required
                          variant="outlined"
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Account Number"
                          fullWidth
                          value={paymentDetails.account_number}
                          onChange={handlePaymentDetailsChange('account_number')}
                          required
                          variant="outlined"
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="IFSC Code"
                          fullWidth
                          value={paymentDetails.ifsc_code}
                          onChange={handlePaymentDetailsChange('ifsc_code')}
                          placeholder="e.g. SBIN0001234"
                          required
                          variant="outlined"
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Branch Name (Optional)"
                          fullWidth
                          value={paymentDetails.branch_name}
                          onChange={handlePaymentDetailsChange('branch_name')}
                          variant="outlined"
                        />
                      </Grid>
                    </>
                  )}

                  {['Google Pay', 'PhonePe', 'Paytm'].includes(paymentMethod) && (
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Registered Mobile Number"
                        fullWidth
                        value={paymentDetails.mobile_number}
                        onChange={handlePaymentDetailsChange('mobile_number')}
                        required
                        variant="outlined"
                      />
                    </Grid>
                  )}
                </>
              )}

              <Grid item xs={12}>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={submittingWithdrawal}
                  sx={{
                    bgcolor: '#82A240',
                    fontWeight: 700,
                    textTransform: 'none',
                    px: 5,
                    py: 1.5,
                    borderRadius: 3,
                    boxShadow: '0 4px 12px rgba(130,162,64,0.2)',
                    '&:hover': { bgcolor: '#718F36', boxShadow: '0 6px 16px rgba(130,162,64,0.3)' }
                  }}
                >
                  {submittingWithdrawal ? (
                    <>
                      <CircularProgress size={20} sx={{ mr: 1, color: '#fff' }} />
                      Processing...
                    </>
                  ) : (
                    'Submit Withdraw Request'
                  )}
                </Button>
              </Grid>
            </Grid>
          </form>
        </CardContent>
      </Card>

      {/* Withdraw Request History Section */}
      <Box sx={{ mb: 5 }}>
        <Typography variant="h6" fontWeight={700} gutterBottom sx={{ mb: 3 }}>
          Withdraw Request History
        </Typography>

        <TableContainer
          component={Paper}
          sx={{
            borderRadius: 4,
            boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
            overflow: 'hidden',
            border: '1px solid',
            borderColor: alpha(theme.palette.divider, 0.1),
          }}
        >
          <Table>
            <TableHead sx={{ bgcolor: alpha(theme.palette.primary.main, 0.04) }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, color: 'text.secondary' }}>Request ID</TableCell>
                <TableCell sx={{ fontWeight: 700, color: 'text.secondary' }}>Request Date</TableCell>
                <TableCell sx={{ fontWeight: 700, color: 'text.secondary' }}>Amount</TableCell>
                <TableCell sx={{ fontWeight: 700, color: 'text.secondary' }}>Payment Method</TableCell>
                <TableCell sx={{ fontWeight: 700, color: 'text.secondary' }}>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {withdrawalRequests.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No withdrawal requests found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                withdrawalRequests.map((req) => (
                  <TableRow
                    key={req.id}
                    sx={{
                      '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.02) },
                      transition: 'background-color 0.2s ease',
                    }}
                  >
                    <TableCell>#{req.id}</TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        {new Date(req.created_at).toLocaleDateString(undefined, {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric'
                        })}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block' }}>
                        {new Date(req.created_at).toLocaleTimeString(undefined, {
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography fontWeight={700}>₹{Number(req.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{req.payment_method}</Typography>
                    </TableCell>
                    <TableCell>
                      {req.status?.toLowerCase() === 'approved' && (
                        <Box sx={{ mt: 0.5 }}>
                          <Chip label="Approved" size="small" color="success" sx={{ fontWeight: 700 }} />
                          <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                            Mode: {req.payment_mode} | Txn ID: {req.transaction_id}
                          </Typography>
                          {req.processed_at && (
                            <Typography variant="caption" display="block" color="text.disabled">
                              Approved: {new Date(req.processed_at).toLocaleString()}
                            </Typography>
                          )}
                        </Box>
                      )}
                      {req.status?.toLowerCase() === 'rejected' && (
                        <Box sx={{ mt: 0.5 }}>
                          <Chip label="Rejected" size="small" color="error" sx={{ fontWeight: 700 }} />
                          <Typography variant="caption" display="block" color="error" sx={{ mt: 0.5 }}>
                            Reason: {req.rejection_reason}
                          </Typography>
                          {req.processed_at && (
                            <Typography variant="caption" display="block" color="text.disabled">
                              Rejected: {new Date(req.processed_at).toLocaleString()}
                            </Typography>
                          )}
                        </Box>
                      )}
                      {req.status?.toLowerCase() === 'pending' && (
                        <Box sx={{ mt: 0.5 }}>
                          <Chip label="Pending" size="small" color="warning" sx={{ fontWeight: 700 }} />
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Transactions Section */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <HistoryIcon /> Transaction History
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {pagination.total > 0 && (
            <Typography variant="body2" color="text.secondary">
              {((pagination.current_page - 1) * pagination.per_page) + 1}–
              {Math.min(pagination.current_page * pagination.per_page, pagination.total)} of {pagination.total}
            </Typography>
          )}
          {loading && <CircularProgress size={20} />}
        </Box>
      </Box>

      <TableContainer
        component={Paper}
        sx={{
          borderRadius: 4,
          boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
          overflow: 'hidden',
          border: '1px solid',
          borderColor: alpha(theme.palette.divider, 0.1),
          // Dim table while loading a new page
          opacity: loading ? 0.6 : 1,
          transition: 'opacity 0.2s ease',
        }}
      >
        <Table>
          <TableHead sx={{ bgcolor: alpha(theme.palette.primary.main, 0.04) }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, color: 'text.secondary' }}>Transaction ID</TableCell>
              <TableCell sx={{ fontWeight: 700, color: 'text.secondary' }}>Date</TableCell>
              <TableCell sx={{ fontWeight: 700, color: 'text.secondary' }}>Type</TableCell>
              <TableCell sx={{ fontWeight: 700, color: 'text.secondary' }}>Amount</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, color: 'text.secondary' }}>Balance</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {transactions.map((tx) => (
              <TableRow
                key={tx.id}
                sx={{
                  '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.02) },
                  transition: 'background-color 0.2s ease',
                }}
              >
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
                      {tx.transaction_id?.substring(0, 8)}...
                    </Typography>
                    <Tooltip title="Copy ID">
                      <IconButton size="small" onClick={() => handleCopyId(tx.transaction_id)}>
                        <ContentCopyIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {new Date(tx.created_at).toLocaleDateString(undefined, {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric'
                    })}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block' }}>
                    {new Date(tx.created_at).toLocaleTimeString(undefined, {
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={tx.transaction_type}
                    size="small"
                    sx={{
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: 1,
                      fontSize: 10,
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      color: theme.palette.primary.main,
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {tx.credit > 0 ? (
                      <Typography sx={{ color: '#4caf50', fontWeight: 700 }}>
                        +₹{tx.credit.toLocaleString()}
                      </Typography>
                    ) : (
                      <Typography sx={{ color: '#ff5252', fontWeight: 700 }}>
                        -₹{tx.debit.toLocaleString()}
                      </Typography>
                    )}
                  </Box>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    ₹{tx.balance.toLocaleString()}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination — only rendered when last_page > 1 */}
      {pagination.last_page > 1 && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            mt: 4,
          }}
        >
          <Pagination
            count={pagination.last_page}
            page={pagination.current_page}
            onChange={handlePageChange}
            disabled={loading}
            color="primary"
            shape="rounded"
            showFirstButton
            showLastButton
            renderItem={(item) => (
              <PaginationItem
                {...item}
                sx={{
                  borderRadius: 2,
                  fontWeight: 600,
                  '&.Mui-selected': {
                    fontWeight: 700,
                  },
                }}
              />
            )}
          />
        </Box>
      )}

      {/* Empty State */}
      {!loading && transactions.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 10 }}>
          <HistoryIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
          <Typography color="text.secondary">No transactions found</Typography>
        </Box>
      )}
    </Box>
  );
};

export default Wallet;